import { Application } from '@splinetool/runtime'
import { useEffect, useRef, useState } from 'react'
import { cn } from '#app/utils/misc.tsx'

interface Props {
	scene: string
	className?: string
	canvasClassName?: string
	overlayClassName?: string
}

export function SplineViewer({
	scene,
	className,
	canvasClassName,
	overlayClassName,
}: Props) {
	const canvasRef = useRef<HTMLCanvasElement>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [hasError, setHasError] = useState(false)

	useEffect(() => {
		if (!canvasRef.current) return

		let spline: Application | null = null

		const loadSpline = async () => {
			try {
				setIsLoading(true)
				setHasError(false)

				const canvas = canvasRef.current
				if (!canvas) return
				spline = new Application(canvas)
				await spline.load(scene)

				setIsLoading(false)
			} catch (error) {
				console.error('Failed to load Spline scene:', error)
				setHasError(true)
				setIsLoading(false)
			}
		}

		void loadSpline()

		return () => {
			if (spline) {
				spline.dispose()
			}
		}
	}, [scene])

	const containerClasses = cn(
		'relative h-full w-full bg-neutral-200',
		className,
	)
	const canvasClasses = cn(
		'h-full w-full bg-neutral-200',
		isLoading && 'opacity-0',
		canvasClassName,
	)

	const overlayClasses = cn(
		'pointer-events-none absolute inset-0 bg-neutral-100/70',
		overlayClassName,
	)

	if (hasError) {
		return (
			<div className={containerClasses}>
				<div className={overlayClasses} />
				<div className="relative z-10 flex h-full w-full items-center justify-center">
					<div className="text-center text-neutral-200">
						<div className="mb-2 font-medium">Failed to load 3D scene</div>
						<div className="text-xs text-neutral-400">
							Check your internet connection
						</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className={containerClasses}>
			<canvas
				ref={canvasRef}
				className={canvasClasses}
				style={{
					display: 'block',
					transition: 'opacity 0.5s ease-in-out',
					opacity: isLoading ? 0 : 1,
				}}
			/>
			<div className={overlayClasses} />
		</div>
	)
}
